/**
 * Рабочий стол исполнителя (ТЗ §8.3) — упрощённый светофор по своим делам.
 *
 * Данные: `GET /api/dashboard` (для роли `executor` бэкенд отдаёт шесть плиток KPI по его
 * делам, полоску «Сегодня», ближайшие сроки, новые за сутки и очередь без исполнителя;
 * `decisions`, `load`, `legal` приходят `null`) и `GET /api/cases` для блока «Мои дела»
 * с фильтрами, поиском и постраничной навигацией.
 *
 * Разметка блоков — из макета: `.kpi-grid`, `.deadline-bar.today-strip`, `.deadline-list`,
 * `.stat-block` со строками `.decision-row` и таблица `.my-row`.
 *
 * Клик по строке открывает карточку дела через `useOpenCase`: адрес меняется на `/cases/:id`,
 * саму модалку поверх экрана рисует `App`, поэтому здесь её нет.
 */
import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useAuth } from '@/auth/AuthContext';
import { useDashboard } from '@/api/hooks';
import { useOpenCase } from '@/components/Modal';
import type { CaseListItem, KpiKey, KpiTile, TodayStripBlock } from '@/api/types';
import { DEFAULT_PERIOD, deadlineClass } from '@/lib/deadlines';
import { caseWord, fmtDate, fmtTime } from '@/lib/format';
import { stageChipClass, stageLabel } from '@/lib/stages';
import { CaseListModal } from '@/components/dashboard/CaseListModal';
import type { ListRequest } from '@/components/dashboard/types';
import { CaseFilters, MY_CHIPS } from '@/components/cases/CaseFilters';
import { useCaseListParams } from '@/components/cases/useCaseListParams';
import { caseFlags, rowKeyDown } from '@/components/cases/CaseRow';
import { CasesTable, useCaseList } from '@/components/cases/CasesTable';
import { Pagination } from '@/components/cases/Pagination';
import { UnassignedQueue } from '@/components/cases/UnassignedQueue';
import { ErrorState, LoadingState, pendingFailure } from '../components/ErrorState';

const KPI_ICONS: Record<string, string> = {
    open: '📁',
    soon: '🔥',
    late: '⏰',
    waiting_draft: '📝',
    in_approval: '🖐',
    new_today: '🆕',
    p1: '⚠️',
    risk: '🛡',
    repeat: '🔁',
    extended: '⏳',
    refund: '💰',
    unassigned: '📥',
    closed: '✅',
};

/** Цвет плитки: ноль по «плохой» метрике — зелёный, как в макете. */
function kpiClassName(tile: KpiTile, clickable: boolean): string {
    const zero = tile.value === 0;
    let tone = 'bordered-blue';
    if (tile.tone === 'bad') tone = zero ? 'green bordered-green' : 'red bordered-red';
    else if (tile.tone === 'warn') tone = zero ? 'bordered-green' : 'yellow bordered-orange';
    else if (tile.tone === 'good') tone = 'green bordered-green';
    return `kpi-card ${tone}${clickable ? ' clickable' : ''}`;
}

function kpiSubClassName(tile: KpiTile): string | undefined {
    if (tile.value === 0) return undefined;
    if (tile.tone === 'bad') return 'highlight-red';
    if (tile.tone === 'warn') return 'highlight-orange';
    if (tile.tone === 'good') return 'highlight-green';
    return undefined;
}

/** «Показать список →»: те же дела, но уже на экране `/cases`. */
function kpiSearch(key: KpiKey, executorId: string): string | null {
    const params = new URLSearchParams();
    if (executorId) params.set('executor', executorId);
    switch (key) {
        case 'open':
            params.set('chip', 'open');
            break;
        case 'soon':
            params.set('chip', 'hot');
            break;
        case 'late':
            params.set('chip', 'late');
            break;
        case 'waiting_draft':
            params.set('chip', 'open');
            params.set('stage', 'draft');
            break;
        case 'in_approval':
            params.set('chip', 'open');
            params.set('stage', 'approval');
            break;
        default:
            // «Новые за сутки» — предикат `created_at >= now() - 24 часа`; у `GET /api/cases`
            // такого фильтра нет, поэтому плитка открывает выборку модалкой (см. openList).
            return null;
    }
    return params.toString();
}

/** Плитки без своего адреса на `/cases`: открываются модалкой `GET /api/dashboard/list`. */
const MODAL_KPI: Readonly<Record<string, string | undefined>> = {
    new_today: 'Новые за сутки',
};

interface KpiGridProps {
    tiles: readonly KpiTile[];
    executorId: string;
    openList: (request: ListRequest) => void;
}

function KpiGrid({ tiles, executorId, openList }: KpiGridProps) {
    const navigate = useNavigate();
    return (
        <div className="kpi-grid" id="kpiGrid">
            {tiles.map((tile) => {
                const search = kpiSearch(tile.key, executorId);
                const modalTitle = MODAL_KPI[tile.key];
                const clickable = tile.value > 0 && (search !== null || modalTitle !== undefined);
                const open = () => {
                    if (search) navigate({ pathname: '/cases', search });
                    else if (modalTitle) openList({ title: modalTitle, kpi: tile.key });
                };
                const subClass = kpiSubClassName(tile);
                return (
                    <div
                        key={tile.key}
                        className={kpiClassName(tile, clickable)}
                        data-kpi={tile.key}
                        role={clickable ? 'button' : undefined}
                        tabIndex={clickable ? 0 : undefined}
                        aria-label={`${tile.label}: ${tile.value}`}
                        onClick={clickable ? open : undefined}
                        onKeyDown={clickable ? (event) => rowKeyDown(event, open) : undefined}
                    >
                        <div className="kpi-top">
                            <span className="kpi-value">{tile.value}</span>
                            <span className="kpi-icon">{KPI_ICONS[tile.key] ?? '📌'}</span>
                        </div>
                        <div className="kpi-label">{tile.label}</div>
                        <div className="kpi-sub">
                            {subClass ? <span className={subClass}>{tile.sub}</span> : tile.sub}
                        </div>
                        <span className="scope-tag">{tile.scope === 'period' ? 'за период' : 'на сейчас'}</span>
                        {clickable ? <div className="kpi-more">Показать список →</div> : null}
                    </div>
                );
            })}
        </div>
    );
}

function TodayStrip({ strip }: { strip: TodayStripBlock }) {
    return (
        <div className="deadline-bar today-strip" id="todayStrip">
            <span className="bar-label">📌 Сегодня</span>
            <span className="bar-stats">
                уходит ответов: <b>{strip.today}</b> · до конца недели: <b>{strip.week}</b> ·{' '}
                {strip.late > 0 ? (
                    <span className="ts-red">просрочено: {strip.late}</span>
                ) : (
                    <span className="ts-green">просрочек нет</span>
                )}{' '}
                ·{' '}
                {strip.extended > 0 ? <span className="ts-orange">продлено: {strip.extended}</span> : 'продлений нет'}
            </span>
        </div>
    );
}

function HotList({
    rows,
    totalOpen,
    onOpen,
}: {
    rows: readonly CaseListItem[];
    totalOpen: number;
    onOpen: (caseId: string) => void;
}) {
    return (
        <div className="deadline-list" id="hotBlock">
            <div className="list-header">
                <h3>🔥 Ближайшие сроки ответа</h3>
                <span className="count">
                    {totalOpen} {caseWord(totalOpen)} в работе
                </span>
            </div>
            <div id="hotList">
                {rows.length === 0 ? (
                    <div className="detail-empty">Открытых дел нет — назначенные вам дела появятся здесь после разбора.</div>
                ) : (
                    rows.map((item) => {
                        const open = () => onOpen(item.case_id);
                        const left = item.days_left;
                        const urgent = typeof left === 'number' && left <= 3;
                        return (
                            <div
                                key={item.case_id}
                                className={urgent ? 'deadline-item urgent-row' : 'deadline-item'}
                                data-case={item.case_id}
                                role="button"
                                tabIndex={0}
                                onClick={open}
                                onKeyDown={(event) => rowKeyDown(event, open)}
                            >
                                <span className="item-desc">
                                    {item.priority === 1 ? '🚨' : '📌'} {item.title}
                                    {item.escalation_risk ? <span title="риск проверки"> 🛡</span> : null}
                                </span>
                                <span className="item-meta">
                                    <span className={stageChipClass(item.stage)}>{stageLabel(item.stage)}</span>
                                    <span className={`item-date ${deadlineClass(item.days_left)}`.trim()}>
                                        {item.deadline_text}
                                    </span>
                                    <span className="item-responsible">
                                        👤 {item.executor_name ?? 'без исполнителя'}
                                    </span>
                                </span>
                            </div>
                        );
                    })
                )}
            </div>
        </div>
    );
}

function NewCases({
    rows,
    count,
    onOpen,
}: {
    rows: readonly CaseListItem[];
    count: number;
    onOpen: (caseId: string) => void;
}) {
    // Пустое состояние в макете особое: блок целиком исчезает.
    if (rows.length === 0) return null;
    // В заголовке настоящий счётчик, строк сервер отдаёт не больше двадцати.
    const rest = count - rows.length;
    return (
        <div className="stat-block" id="newBlock" style={{ marginBottom: 18 }}>
            <div className="block-header">
                <span>
                    🆕 Новые за сутки{' '}
                    <span style={{ textTransform: 'none', fontWeight: 500, color: '#56697f' }}>· {count}</span>
                </span>
            </div>
            <div className="block-body" id="newList">
                {rows.map((item) => {
                    const open = () => onOpen(item.case_id);
                    return (
                        <div
                            key={item.case_id}
                            className="decision-row"
                            data-case={item.case_id}
                            role="button"
                            tabIndex={0}
                            onClick={open}
                            onKeyDown={(event) => rowKeyDown(event, open)}
                        >
                            <span className="dr-reason approval">новое</span>
                            <span className="dr-id">{item.number}</span>
                            <span className="dr-title">
                                {item.title}
                                {caseFlags(item)}
                            </span>
                            <span className="dr-exec">{item.applicant ?? 'заявитель не распознан'}</span>
                            <span className={`dr-date ${deadlineClass(item.days_left)}`.trim()}>
                                {item.deadline_text}
                            </span>
                        </div>
                    );
                })}
                {rest > 0 ? (
                    <div className="case-more">
                        и ещё {rest} {caseWord(rest)}
                    </div>
                ) : null}
            </div>
        </div>
    );
}

export function DashboardExecutor() {
    const openCase = useOpenCase();
    // «Мои дела»: фильтры, поиск и страница — в адресе страницы (обновление, «назад» и ссылка сохраняют выборку).
    const { filters, page, limit, changeFilters, changeParam } = useCaseListParams(MY_CHIPS);
    const [listRequest, setListRequest] = useState<ListRequest | null>(null);

    const openList = useCallback((request: ListRequest) => {
        setListRequest(request);
    }, []);
    const closeList = useCallback(() => {
        setListRequest(null);
    }, []);

    const { user, isExecutor } = useAuth();
    const dashboard = useDashboard(DEFAULT_PERIOD);
    // Ошибка — сразу после первой неудачи; повторы идут в фоне, при успехе блок с ошибкой уходит сам.
    const failure = pendingFailure(dashboard);

    const meId = user?.id ?? '';

    // «Мои дела» — всегда дела текущего пользователя, чип и поиск только уточняют выборку.
    const query = useMemo(
        () => ({ ...filters, executor: meId, page, limit, sort: 'deadline' as const }),
        [filters, meId, page, limit],
    );
    const cases = useCaseList(query, meId.length > 0);

    const data = dashboard.data;
    const openTile = data?.kpi.find((tile) => tile.key === 'open');
    const openCount = openTile?.value ?? 0;
    const scopeText = user ? `· ${user.full_name}${user.post ? `, ${user.post}` : ''}` : '';

    return (
        <>
            <div className="page-header">
                <h1>
                    🗂 Рабочий стол исполнителя
                    {data ? <span>данные на {fmtDate(data.generated_at)}</span> : null}
                </h1>
                {data ? (
                    <span className="update-info">🔄 обновлено: {fmtTime(data.generated_at)}</span>
                ) : null}
            </div>

            {failure ? (
                <ErrorState error={failure} title="Не удалось загрузить рабочий стол" onRetry={() => void dashboard.refetch()}>
                    {dashboard.isFetching ? <span className="retry-note">Повторяем запрос автоматически…</span> : null}
                </ErrorState>
            ) : null}

            {!data && !failure && dashboard.isPending ? (
                <div className="stat-block" style={{ marginBottom: 18 }}>
                    <div className="block-body">
                        <LoadingState variant="inline" label="Загрузка рабочего стола…" />
                    </div>
                </div>
            ) : null}

            {data ? (
                <>
                    <KpiGrid tiles={data.kpi} executorId={meId} openList={openList} />
                    {data.today_strip ? <TodayStrip strip={data.today_strip} /> : null}
                    <HotList rows={data.hot.rows} totalOpen={data.hot.total_open} onOpen={openCase} />
                    <NewCases rows={data.new_today.rows} count={data.new_today.count} onOpen={openCase} />
                    <UnassignedQueue
                        rows={data.unassigned.rows}
                        count={data.unassigned.count}
                        canTake={isExecutor}
                        onOpen={openCase}
                    />
                </>
            ) : null}

            <CaseFilters value={filters} onChange={changeFilters} chips={MY_CHIPS} />

            <div className="stat-block">
                <div className="block-header">
                    <span>
                        📁 Мои дела{' '}
                        <span style={{ textTransform: 'none', fontWeight: 500, color: '#56697f' }}>{scopeText}</span>
                    </span>
                </div>
                <div className="block-body" id="myBlock" aria-busy={cases.isFetching}>
                    <CasesTable
                        items={cases.items}
                        variant="my"
                        onOpen={openCase}
                        loading={cases.isPending}
                        error={cases.error}
                    />
                    {cases.truncated ? (
                        <div className="case-more">
                            Выборка «Повторные» приходит с сервера не более чем 200 делами: всего таких
                            дел {cases.serverTotal}. Уточните поиск, чтобы увидеть остальные.
                        </div>
                    ) : null}
                </div>
                <Pagination
                    page={cases.page}
                    limit={cases.limit}
                    total={cases.total}
                    pages={cases.pages}
                    onPage={(next) => changeParam('page', next)}
                    onLimit={(next) => changeParam('limit', next)}
                    left={
                        <span>
                            У исполнителя открыто: <b>{openCount}</b> {caseWord(openCount)} · показано{' '}
                            <b>{cases.items.length}</b> из <b>{cases.total}</b>
                        </span>
                    }
                />
            </div>

            {listRequest ? (
                <CaseListModal
                    title={listRequest.title}
                    kpi={listRequest.kpi}
                    params={listRequest.params}
                    period={DEFAULT_PERIOD}
                    onClose={closeList}
                />
            ) : null}
        </>
    );
}

export default DashboardExecutor;
